import { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { MessageCircle, Send, Minimize2 } from "lucide-react";
import { SettingsContext } from "@/App";

const API = "/api";

function getUser() {
  try { return JSON.parse(localStorage.getItem('user_data')); } catch { return null; }
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const { settings: siteSettings } = useContext(SettingsContext);
  const { t } = useTranslation();
  const user = getUser();

  useEffect(() => {
    if (!user) return;
    const fetchMsgs = () => {
      axios.get(`${API}/chat/messages`, { withCredentials: true }).then(res => {
        setMessages(res.data);
        if (!open) {
          const unreadCount = res.data.filter(m => m.sender === 'admin' && !m.read).length;
          setUnread(unreadCount);
        }
      }).catch(() => {});
    };
    fetchMsgs();
    const poll = setInterval(fetchMsgs, 5000);
    return () => clearInterval(poll);
  }, [open, user]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    if (!newMsg.trim()) return;
    await axios.post(`${API}/chat/send`, { message: newMsg }, { withCredentials: true });
    setNewMsg("");
    const res = await axios.get(`${API}/chat/messages`, { withCredentials: true });
    setMessages(res.data);
  };

  if (!user || !user.has_membership || siteSettings?.membership_enabled === false) return null;

  return (
    <>
      {/* Chat bubble */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setUnread(0); }}
          data-testid="chat-widget-toggle"
          className="fixed bottom-20 lg:bottom-6 right-5 z-50 w-14 h-14 bg-gradient-to-br from-[#FFC928] to-[#FFD85A] text-black rounded-full shadow-[0_0_20px_rgba(255,201,40,0.3)] flex items-center justify-center hover:shadow-[0_0_30px_rgba(255,201,40,0.5)] transition-all hover:scale-105"
        >
          <MessageCircle size={24} />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{unread}</span>
          )}
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-20 lg:bottom-6 right-5 z-50 w-80 sm:w-96 h-[28rem] backdrop-blur-xl bg-[#0F172A]/95 border border-white/[0.08] rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] flex flex-col overflow-hidden" data-testid="chat-widget">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black">
            <span className="font-semibold text-sm">Support Chat</span>
            <button onClick={() => setOpen(false)} className="hover:bg-black/10 p-1 rounded transition-colors" data-testid="chat-minimize-btn"><Minimize2 size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {messages.length === 0 && (
              <div className="text-center text-[#94A3B8] text-xs py-8">{t("chat.startChatting")}</div>
            )}
            {messages.map(m => (
              <div key={m.id} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${m.sender === 'user' ? 'bg-[#FFC928] text-black' : 'bg-[#172033] text-white border border-white/[0.06]'}`}>
                  {m.message}
                  <div className={`text-[10px] mt-0.5 ${m.sender === 'user' ? 'text-black/50' : 'text-[#94A3B8]'}`}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="p-2 border-t border-white/[0.06]">
            <div className="flex gap-2">
              <input
                data-testid="chat-input"
                value={newMsg} onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder={t("chat.typePlaceholder")}
                className="flex-1 px-3 py-2 bg-[#050814] border border-white/[0.06] rounded-xl text-white text-sm placeholder-[#94A3B8]/60 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20"
              />
              <button onClick={send} data-testid="chat-send-btn" className="px-3 py-2 bg-[#FFC928] text-black rounded-xl hover:bg-[#FFD85A] transition-colors">
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
